import * as THREE from 'three';
import type { Particles } from './effects';

export interface BossFinisherProfile {
  id: string;
  title: string;
  color: number;
  secondary: number;
  form: 'bloom' | 'shell' | 'meteor' | 'shatter' | 'pyre' | 'collapse' | 'dragon' | 'gears' | 'shutdown' | 'rewind';
  duration: number;
  warp: number;
  shake: number;
  /** Reserved replacement for a future free/CC0 authored finisher asset. */
  gltf?: string;
}

export const BOSS_FINISHERS: readonly BossFinisherProfile[] = [
  { id: 'p1_colony', title: 'COLONY COLLAPSED', color: 0xff3fb0, secondary: 0x9dff3a, form: 'bloom', duration: 2.1, warp: .22, shake: .8 },
  { id: 'c2_anomalocaris_prime', title: 'PRIME SHELL SUNDERED', color: 0xff7a3c, secondary: 0xffd23c, form: 'shell', duration: 2, warp: .18, shake: .85 },
  { id: 'd3_trex', title: 'TYRANT FALLEN', color: 0xff3b3b, secondary: 0xff9a1f, form: 'meteor', duration: 2.2, warp: .2, shake: 1.05 },
  { id: 'i4_mammoth_king', title: 'WINTER CROWN SHATTERED', color: 0x8fd6ff, secondary: 0xffffff, form: 'shatter', duration: 2.25, warp: .2, shake: .9 },
  { id: 's5_chieftain', title: 'ASHBORN EXTINGUISHED', color: 0xff5a2a, secondary: 0xffd36b, form: 'pyre', duration: 2.15, warp: .25, shake: .95 },
  { id: 'e6_colossus', title: 'COLOSSUS BROKEN', color: 0xff3f5f, secondary: 0xe7bd79, form: 'collapse', duration: 2.4, warp: .16, shake: 1.15 },
  { id: 'm7_dragon', title: 'DRAGON VANQUISHED', color: 0xff3a2a, secondary: 0xffc43d, form: 'dragon', duration: 2.35, warp: .28, shake: 1.1 },
  { id: 'n8_automaton', title: 'AUTOMATON DISMANTLED', color: 0xffb020, secondary: 0x8cecff, form: 'gears', duration: 2.3, warp: .18, shake: 1 },
  { id: 'c9_warmech', title: 'WAR MECH OFFLINE', color: 0xff2ad4, secondary: 0x45f6ff, form: 'shutdown', duration: 2.2, warp: .35, shake: 1.05 },
  { id: 't10_devourer', title: 'THE LAST HOUR RECLAIMED', color: 0xff5cf5, secondary: 0xffe7a8, form: 'rewind', duration: 2.8, warp: .72, shake: 1.2 },
] as const;

export const BOSS_FINISHER_BY_ID = new Map(BOSS_FINISHERS.map((profile) => [profile.id, profile]));

interface ActiveFinisher { profile: BossFinisherProfile; group: THREE.Group; x: number; z: number; age: number; pulse: number }

/** Presentation-only staged boss deaths. Rewards remain deterministic and immediate in the sim. */
export class BossFinishers {
  private readonly active: ActiveFinisher[] = [];

  constructor(private readonly scene: THREE.Scene, private readonly particles: Particles) {}

  start(id: string, x: number, z: number): BossFinisherProfile | null {
    const profile = BOSS_FINISHER_BY_ID.get(id);
    if (!profile) return null;
    const group = new THREE.Group();
    group.position.set(x, .18, z);
    const core = new THREE.Mesh(this.geometry(profile.form), new THREE.MeshBasicMaterial({ color: profile.color, transparent: true, opacity: .9, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    core.name = 'core';
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1, .08, 8, profile.form === 'gears' ? 12 : 40), new THREE.MeshBasicMaterial({ color: profile.secondary, transparent: true, opacity: .8, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    ring.name = 'ring';
    ring.rotation.x = Math.PI / 2;
    group.add(core, ring);
    this.scene.add(group);
    this.active.push({ profile, group, x, z, age: 0, pulse: 0 });
    this.particles.burst(x, z, profile.form === 'collapse' ? 90 : 64, profile.color, profile.form === 'shatter' ? 14 : 9, 1, .26, 7);
    return profile;
  }

  update(dt: number, reducedMotion = false): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const active = this.active[i];
      active.age += dt;
      const t = Math.min(1, active.age / active.profile.duration);
      const core = active.group.getObjectByName('core') as THREE.Mesh;
      const ring = active.group.getObjectByName('ring') as THREE.Mesh;
      const reverse = active.profile.form === 'rewind' || active.profile.form === 'collapse';
      const scale = reverse ? 2.8 * (1 - t) + .2 : .35 + t * (active.profile.form === 'dragon' ? 4.8 : 3.4);
      core.scale.setScalar(scale);
      core.rotation.y += dt * (active.profile.form === 'gears' ? 10 : active.profile.form === 'rewind' ? -7 : 4);
      core.rotation.z += dt * (active.profile.form === 'shatter' ? 8 : 1.5);
      ring.scale.setScalar(.7 + t * (active.profile.form === 'meteor' ? 6 : 4));
      ring.rotation.z += dt * (active.profile.form === 'rewind' ? -5 : 3);
      (core.material as THREE.MeshBasicMaterial).opacity = .9 * (1 - t);
      (ring.material as THREE.MeshBasicMaterial).opacity = .8 * (1 - t);
      const pulse = Math.floor(t * (reducedMotion ? 2 : active.profile.form === 'bloom' ? 8 : 5));
      if (pulse > active.pulse) {
        active.pulse = pulse;
        const a = pulse * 2.399;
        const radius = active.profile.form === 'bloom' ? pulse * .5 : pulse * .25;
        this.particles.burst(active.x + Math.cos(a) * radius, active.z + Math.sin(a) * radius, reducedMotion ? 5 : 12, pulse % 2 ? active.profile.secondary : active.profile.color, 5 + pulse, .55, .16, active.profile.form === 'pyre' ? 9 : 4);
      }
      if (t >= 1) {
        this.dispose(active.group);
        this.active.splice(i, 1);
      }
    }
  }

  private geometry(form: BossFinisherProfile['form']): THREE.BufferGeometry {
    switch (form) {
      case 'bloom': return new THREE.IcosahedronGeometry(1, 1);
      case 'shell': return new THREE.SphereGeometry(1, 8, 5, 0, Math.PI * 1.7);
      case 'meteor': return new THREE.ConeGeometry(.8, 2.4, 6);
      case 'shatter': return new THREE.OctahedronGeometry(1.25, 0);
      case 'pyre': return new THREE.ConeGeometry(1.1, 2.8, 7);
      case 'collapse': return new THREE.BoxGeometry(1.8, 2.4, 1.8, 2, 3, 2);
      case 'dragon': return new THREE.TetrahedronGeometry(1.4, 1);
      case 'gears': return new THREE.TorusKnotGeometry(.75, .22, 48, 7, 2, 3);
      case 'shutdown': return new THREE.BoxGeometry(2.2, .22, 2.2);
      case 'rewind': return new THREE.TorusKnotGeometry(.8, .18, 56, 8, 3, 2);
    }
  }

  private dispose(group: THREE.Group): void {
    this.scene.remove(group);
    group.traverse((object) => {
      const mesh = object as THREE.Mesh;
      mesh.geometry?.dispose();
      if (mesh.material) for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) material.dispose();
    });
  }
}
